/**
 * 공용 AI 서비스
 * 서버의 Gemini API와 Socket.IO를 통해 통신하는 싱글톤 서비스
 */

import SocketService from "network/SocketService";
import {
  AIProtocol,
  AIRequest,
  AIResponse,
  AIRequestType,
} from "network/AIProtocol";

// UUID 생성 헬퍼 함수
const generateUUID = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

type AICallback<T> = (response: AIResponse<T>) => void;

class AIService {
  private static instance: AIService;
  private pendingRequests: Map<string, AICallback<unknown>> = new Map();
  private initialized = false;

  public static getInstance(): AIService {
    if (!AIService.instance) {
      AIService.instance = new AIService();
    }
    return AIService.instance;
  }

  private getSocket() {
    return SocketService.getInstance();
  }

  /**
   * AI 서비스 리스너 초기화
   * 앱 시작 시 또는 소켓 연결 후 호출
   */
  public initialize(): void {
    if (this.initialized) return;

    const socket = this.getSocket();

    socket.on(AIProtocol.RESPONSE, (response: AIResponse<unknown>) => {
      const callback = this.pendingRequests.get(response.requestId);
      if (callback) {
        callback(response);
        this.pendingRequests.delete(response.requestId);
      }
    });

    socket.on(AIProtocol.ERROR, (response: AIResponse<unknown>) => {
      const callback = this.pendingRequests.get(response.requestId);
      if (callback) {
        callback({ ...response, success: false });
        this.pendingRequests.delete(response.requestId);
      }
    });

    this.initialized = true;
  }

  /**
   * AI 요청 전송
   * @param type - 요청 타입 (fight_simulation, general 등)
   * @param prompt - AI에게 보낼 프롬프트
   * @param context - 추가 컨텍스트 (선택)
   * @param timeoutMs - 타임아웃 (기본 30초)
   * @returns Promise<AIResponse<T>>
   */
  public request<T = unknown>(
    type: AIRequestType,
    prompt: string,
    context?: Record<string, unknown>,
    timeoutMs: number = 30000
  ): Promise<AIResponse<T>> {
    // 초기화되지 않은 경우 자동 초기화
    if (!this.initialized) {
      this.initialize();
    }

    return new Promise((resolve) => {
      const requestId = generateUUID();

      this.pendingRequests.set(
        requestId,
        resolve as AICallback<unknown>
      );

      const request: AIRequest = {
        requestId,
        type,
        prompt,
        context,
      };

      this.getSocket().emit(AIProtocol.REQUEST, request);

      // 타임아웃 처리
      setTimeout(() => {
        if (this.pendingRequests.has(requestId)) {
          this.pendingRequests.delete(requestId);
          resolve({
            requestId,
            success: false,
            data: null as T,
            error: "Request timeout",
          });
        }
      }, timeoutMs);
    });
  }

  /**
   * 리스너 정리
   */
  public cleanup(): void {
    const socket = this.getSocket();
    socket.off(AIProtocol.RESPONSE);
    socket.off(AIProtocol.ERROR);
    this.pendingRequests.clear();
    this.initialized = false;
  }

  /**
   * 대기 중인 요청 수 반환
   */
  public getPendingRequestCount(): number {
    return this.pendingRequests.size;
  }
}

export default AIService;
